import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { FormSelect } from "../../../forms/FormSelect";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { getAddressParts } from "../../../utils/format";
import { selectSavedAddressFields } from "../helper-utils";
import { setFields } from "../reducers";

interface IProps {
    prefix: "shipping" | "billing";
    isDisabled?: boolean;
}

export const SavedAddressSelector = (props: IProps): React.ReactNode => {
    const dispatch = useAppDispatch();
    const form = useAppSelector((state) => state.checkout.form);
    const savedAddresses = useAppSelector(
        (state) => state.checkout.data.addresses,
    );

    const addresses = savedAddresses
        .map((addr) => {
            const parts = getAddressParts({
                line1: addr.line1,
                line2: addr.line2,
                city: addr.line4,
                state: addr.state,
                postal: addr.postcode,
            });
            const name = `${addr.title} ${addr.first_name} ${addr.last_name}`
                .replace(/\s+/g, " ")
                .trim();
            const line1 = parts.streetLine1;
            const line2 = parts.streetLine2;
            const cityState = parts.cityStatePostal;
            // Separate templates allow translators to reorder parts and change separators
            const descr = line2
                ? interpolate(t`%s, %s, %s, %s`, [
                      name,
                      line1,
                      line2,
                      cityState,
                  ])
                : interpolate(t`%s, %s, %s`, [name, line1, cityState]);
            return {
                value: addr.url,
                label: descr.replace(/\s+/g, " ").trim(),
            };
        })
        .sort((a, b) => {
            if (a.label === b.label) {
                return 0;
            }
            return a.label > b.label ? 1 : -1;
        });
    // Add a blank option to the top
    addresses.unshift({
        value: "",
        label: "",
    });
    const getSavedAddressOptions = addresses;

    const onSavedAddressChange = (
        event: React.FormEvent<HTMLSelectElement>,
    ) => {
        const fields = selectSavedAddressFields(
            props.prefix,
            savedAddresses,
            event.currentTarget.value,
        );
        dispatch(setFields(fields));
    };

    if (savedAddresses.length <= 0) {
        return null;
    }

    const elemID = `${props.prefix}_address_url`;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const addrURL = `${props.prefix}_address_url` as
        | "shipping_address_url"
        | "billing_address_url";
    const value = form[addrURL];

    return (
        <FormSelect
            label={t`Saved Address`}
            labelPlacement="dynamic"
            name={elemID}
            id={elemID}
            value={value}
            onChange={onSavedAddressChange}
            choices={getSavedAddressOptions}
            disabled={props.isDisabled}
        />
    );
};
